import {
  EllipsisVerticalIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState, useRef } from 'react';

export interface MenuOption {
  title: string;
  value: string;
  color?: string;
  onClick: () => void;
}

interface MenuButtonProps {
  options: MenuOption[];
  direction?: 'left' | 'right' | 'top' | 'bottom';
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export function MenuButton({
  options,
  direction = 'right',
  orientation = 'vertical',
  className,
}: MenuButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleBlur = (event: React.FocusEvent): void => {
    if (!containerRef.current?.contains(event.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  const getMenuPosition = (): string => {
    switch (direction) {
      case 'left':
        return 'right-full mr-1 top-0';
      case 'right':
        return 'left-full ml-2 top-0';
      case 'top':
        return 'bottom-full mb-2';
      case 'bottom':
        return 'top-full mt-2';
      default:
        return 'right-0';
    }
  };

  return (
    <div
      className="relative"
      ref={containerRef}
      onBlur={handleBlur}
      tabIndex={-1}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx('p-1 rounded-full outline-none', className)}
      >
        {orientation === 'vertical' ? (
          <EllipsisVerticalIcon className="w-5 h-5 text-gray-900" />
        ) : (
          <EllipsisHorizontalIcon className="w-5 h-5 text-gray-900" />
        )}
      </button>

      {isOpen && (
        <div
          className={clsx(
            'absolute z-50',
            'flex flex-col items-start',
            'w-48 p-1',
            'rounded-md',
            'bg-white',
            'shadow-lg',
            getMenuPosition(),
          )}
        >
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => {
                option.onClick();
                setIsOpen(false);
              }}
              className={clsx(
                'flex items-center w-full',
                'h-10 p-4 ',
                'text-base font-normal leading-[22px]',
                'text-left text-gray-900',
                'hover:text-orange-500',
              )}
            >
              {option.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
